import React from "react";
import { Section, Text, Link } from "@/components";
import { Paragraph, LineBreak, Quote, SubHeading } from "./Common";

export const TermsOfService = (): JSX.Element => {
  return (
    <div className="h-[calc(100vh-152px)] overflow-y-auto">
      <Section className="mx-auto max-w-[1191px] justify-start">

        <div className="mb-14">
          <Text.Heading className="text-left mb-6">Terms of Service</Text.Heading>
          <Paragraph>
            Updated: March 31st 2025
            <LineBreak />
            Welcome to <Link to="https://www.intelliroom.ai" className="!p-0"><Quote>www.intelliroom.ai</Quote></Link>
            ("<Quote>T12</Quote>", "<Quote>we</Quote>", "<Quote>us</Quote>") 
            These Terms of Service (“<Quote>Terms</Quote>”) govern your access to and use of our website at 
            <Link to="https://www.intelliroom.ai" className="!p-0"><Quote>www.intelliroom.ai</Quote></Link> 
            including any related applications, tools, services, and content provided by us (collectively, the “<Quote>Services</Quote>”).

            <LineBreak />
            By accessing or using the Services, you (“<Quote>you</Quote>” or “<Quote>User</Quote>”) agree to be bound by these Terms and our Privacy Policy. 
            If you do not agree to these Terms, do not use our Services.
          </Paragraph>
        </div>
                
        <div className="mb-14">
          <SubHeading>1.Eligibility</SubHeading>
          <Paragraph>
            You must be at least 18 years old and legally capable of entering into a binding contract to use our Services. If you are using the Services on behalf of an organization, you represent and warrant that you have the authority to bind that organization to these Terms.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>2.Account Registration and Security</SubHeading>
          <Paragraph>
            To access certain features, you may be required to create an account. You agree to:
            <ul className="list-disc list-inside ml-12">
              <li>
                Provide accurate, current, and complete information during registration.
              </li>
              <li>
                Maintain the security of your login credentials.
              </li>
              <li>
                Notify us immediately of any unauthorized access or use of your account.
              </li>
              <li>Be responsible for all activities under your account.</li>
            </ul>
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>3. Use of Services</SubHeading>
          <Paragraph>
            You agree to use the Services only for lawful purposes and in accordance with these Terms. You may not:
            <ul className="list-disc list-inside ml-12">
              <li>Reverse-engineer, decompile, or otherwise attempt to extract the source code of our Services.</li>
              <li>Use automated scripts to collect information or interact with the Services.</li>
              <li>Use the Services to develop or train competing AI or due diligence technologies.</li>
              <li>Transmit viruses, malware, or other harmful software.</li>
            </ul>
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>4. License</SubHeading>
          <Paragraph>
            Subject to these Terms, we grant you a limited, non-exclusive, non-transferable, revocable license to access and use the Services for your internal business purposes.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>5. Intellectual Property</SubHeading>
          <Paragraph>
            All rights, title, and interest in and to the Services (excluding your User Content) are and will remain the exclusive property of IntelliRoom™ and its licensors. You agree not to use our trademarks, logos, or other proprietary materials without our prior written consent.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>6. User Content</SubHeading>
          <Paragraph>
            You retain ownership of any content, data, or materials you submit to the Services (“User Content”).
            <LineBreak />
            By submitting User Content, you grant IntelliRoom™ a non-exclusive, royalty-free, worldwide license to use, host, process, and display such content for the purpose of operating and improving the Services.
            <LineBreak />
            You represent that you have all rights necessary to submit the User Content and that it does not infringe on any third-party rights.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>7. Confidentiality</SubHeading>
          <Paragraph>
            Both parties agree to keep confidential any non-public, proprietary information disclosed in the course of using the Services. This includes deal documents, diligence data, and other sensitive information shared within IntelliRoom™.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>8. AI-Powered Features and Limitations</SubHeading>
          <Paragraph>
            The Services may include features powered by artificial intelligence. While we aim to provide accurate and useful outputs, you acknowledge that:
            <ul className="list-disc list-inside ml-12">
              <li>AI-generated results may contain errors or bias.</li>
              <li>The Services do not constitute legal, financial, or investment advice.</li>
              <li>Final decisions should not be made solely based on AI-generated outputs.</li>
            </ul>
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>9. Fees and Payment</SubHeading>
          <Paragraph>
            If you purchase a subscription or other paid Services, you agree to pay all applicable fees. All payments are non-refundable unless otherwise stated in writing.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>10. Termination</SubHeading>
          <Paragraph>
            We may suspend or terminate your access to the Services at any time, with or without notice, if you violate these Terms or if we discontinue the Services. You may terminate your account at any time by contacting us at support@intelliroom.ai.
            <LineBreak />
            Upon termination, your license to use the Services ends immediately, but sections related to Intellectual Property, Confidentiality, Limitation of Liability, and Governing Law will survive.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>11. Disclaimer of Warranties</SubHeading>
          <Paragraph>
            The Services are provided “<Quote>AS IS</Quote>” and “<Quote>AS AVAILABLE</Quote>” without warranties of any kind. We disclaim all warranties, express or implied, including merchantability, fitness for a particular purpose, and non-infringement.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>12. Limitation of Liability</SubHeading>
          <Paragraph>
            To the maximum extent permitted by law, IntelliRoom™ shall not be liable for any indirect, incidental, special, or consequential damages, including lost profits or data, arising out of or in connection with your use of the Services.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>13. Indemnification</SubHeading>
          <Paragraph>
            You agree to indemnify and hold harmless IntelliRoom™ and its affiliates from any claims, damages, or liabilities arising from your use of the Services, your User Content, or your violation of these Terms.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>14. Changes to Terms</SubHeading>
          <Paragraph>
            We may modify these Terms at any time. We will notify you of material changes via the Services or email. Continued use of the Services after changes become effective constitutes your acceptance of the updated Terms.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>15. Governing Law</SubHeading>
          <Paragraph>
            These Terms shall be governed by the laws of the State of Texas, without regard to its conflict of laws rules. Any disputes shall be resolved exclusively in the courts located in Travis County, Texas.
          </Paragraph>
        </div>

        <div className="mb-14">
          <SubHeading>16. Contact Us</SubHeading>
          <Paragraph>
            If you have any questions about these Terms, please contact us at<br />
            <Link to="mailto:support@intelliroom.ai" className="!p-0"><Quote>support@intelliroom.ai</Quote></Link>
          </Paragraph>
        </div>
      </Section>
    </div>
  );
};
